import { useState, useRef, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { motion, AnimatePresence } from 'framer-motion'
import { MessageSquareText, X, RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useChatBot } from '@/contexts/ChatBotContext'
import { useBackgroundContrast } from '@/hooks/useBackgroundContrast'
import { ChatMessage } from './ChatMessage'
import { ChatInput } from './ChatInput'
import { QuickSuggestions } from './QuickSuggestions'
import { quickSuggestions } from './config'
import type { ChatMessage as ChatMessageType, ChatState } from '@brochure/shared'

export function ChatBot() {
  const { t, i18n } = useTranslation('common')
  const { isOpen, setIsOpen } = useChatBot()
  const [messages, setMessages] = useState<ChatMessageType[]>([])
  const [chatState, setChatState] = useState<ChatState>('idle')
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const floatingButtonRef = useRef<HTMLButtonElement>(null)
  const background = useBackgroundContrast(floatingButtonRef, { containerRef })
  const isDarkBackground = background === 'dark'

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [])

  useEffect(() => {
    scrollToBottom()
  }, [messages, scrollToBottom])

  useEffect(() => {
    if (isOpen) {
      // Focus input when chat opens
      setTimeout(() => inputRef.current?.focus(), 300)
    }
  }, [isOpen])

  const generateId = () =>
    `msg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`

  const sendMessage = async (content: string) => {
    if (!content.trim() || chatState === 'loading') return

    const userMessage: ChatMessageType = {
      id: generateId(),
      role: 'user',
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages((prev) => [...prev, userMessage])
    setChatState('loading')

    try {
      const response = await fetch('/api/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: content,
          history: messages.map((m) => ({ role: m.role, content: m.content })),
          language: i18n.language,
        }),
      })

      const data = await response.json()

      const assistantMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: data.response || data.error || t('chatbot.error'),
        timestamp: new Date(data.timestamp || Date.now()),
      }

      setMessages((prev) => [...prev, assistantMessage])
      setChatState('idle')
    } catch (error) {
      console.error('Chat error:', error)
      setChatState('error')

      const errorMessage: ChatMessageType = {
        id: generateId(),
        role: 'assistant',
        content: t('chatbot.error'),
        timestamp: new Date(),
      }
      setMessages((prev) => [...prev, errorMessage])
    }
  }

  const handleReset = () => {
    setMessages([])
    setChatState('idle')
  }

  const handleSuggestionSelect = (messageKey: string) => {
    const message = t(messageKey)
    sendMessage(message)
  }

  return (
    <div ref={containerRef} className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6" style={{ zIndex: 9999 }}>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 16, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 16, scale: 0.96 }}
            transition={{ duration: 0.24, ease: [0.16, 1, 0.3, 1] }}
            className={cn(
              'absolute bottom-16 sm:bottom-20 right-0',
              'w-[calc(100vw-32px)] sm:w-[380px] max-w-[380px]',
              'bg-white rounded-md overflow-hidden',
              'border border-ink-200 shadow-[0_24px_48px_-16px_rgba(18,52,82,0.25)]',
              'flex flex-col',
              'max-h-[calc(100vh-100px)] sm:max-h-[calc(100vh-120px)]'
            )}
          >
            {/* Header — industrial brand band */}
            <div className="relative bg-brand-600 text-white px-4 py-3.5 flex items-center justify-between border-b border-brand-700">
              {/* Subtle technical accent line on the left edge */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-brand-400" aria-hidden="true" />
              <div className="pl-2">
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-1.5 h-1.5 rounded-full bg-emerald-400 shadow-[0_0_0_3px_rgba(52,211,153,0.25)]"
                    aria-hidden="true"
                  />
                  <h3 className="font-semibold text-[15px] tracking-tight leading-none">
                    {t('chatbot.title')}
                  </h3>
                </div>
                <p className="text-[11px] uppercase tracking-[0.12em] mt-1 text-brand-100/90 font-medium">
                  {t('chatbot.subtitle')}
                </p>
              </div>
              <button
                onClick={handleReset}
                className="p-1.5 hover:bg-white/15 rounded-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                aria-label={t('chatbot.newConversation')}
                title={t('chatbot.newConversation')}
              >
                <RotateCcw className="w-4 h-4" />
              </button>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 min-h-[220px] max-h-[400px] bg-ink-100">
              {messages.length === 0 ? (
                <div className="text-center text-ink-600 py-4">
                  <p className="mb-5 text-sm leading-relaxed px-2">
                    {t('chatbot.welcome')}
                  </p>
                  <p className="mb-3 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-semibold">
                    {t('chatbot.quickSuggestionsEyebrow')}
                  </p>
                  <QuickSuggestions
                    suggestions={quickSuggestions}
                    onSelect={handleSuggestionSelect}
                    disabled={chatState === 'loading'}
                  />
                </div>
              ) : (
                <>
                  {messages.map((message) => (
                    <ChatMessage key={message.id} message={message} />
                  ))}
                  {chatState === 'loading' && (
                    <ChatMessage
                      message={{
                        id: 'typing',
                        role: 'assistant',
                        content: '',
                        timestamp: new Date(),
                      }}
                      isTyping
                    />
                  )}
                </>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            <ChatInput
              ref={inputRef}
              onSend={sendMessage}
              disabled={chatState === 'loading'}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* Floating Button — Fenix industrial blue */}
      <motion.button
        ref={floatingButtonRef}
        onClick={() => setIsOpen(!isOpen)}
        whileHover={{ scale: 1.04 }}
        whileTap={{ scale: 0.94 }}
        className={cn(
          'relative w-14 h-14 sm:w-[60px] sm:h-[60px] rounded-full',
          'flex items-center justify-center transition-colors duration-300',
          'shadow-[0_10px_28px_-6px_rgba(18,52,82,0.45)]',
          'focus:outline-none focus-visible:ring-4 focus-visible:ring-brand-300/60',
          isDarkBackground
            ? 'bg-white hover:bg-ink-100 ring-1 ring-brand-200'
            : 'bg-brand-500 hover:bg-brand-600 ring-1 ring-brand-600/40'
        )}
        aria-label={isOpen ? t('chatbot.close') : t('chatbot.open')}
      >
        {/* Notification ping — only when closed, draws attention once */}
        {!isOpen && (
          <span
            className="absolute -top-0.5 -right-0.5 flex h-2.5 w-2.5"
            aria-hidden="true"
          >
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-brand-300 opacity-60" />
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-brand-400 ring-2 ring-white" />
          </span>
        )}
        <AnimatePresence mode="wait">
          {isOpen ? (
            <motion.div
              key="close"
              initial={{ rotate: -90, opacity: 0 }}
              animate={{ rotate: 0, opacity: 1 }}
              exit={{ rotate: 90, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <X
                className={cn(
                  'w-5 h-5 sm:w-6 sm:h-6',
                  isDarkBackground ? 'text-brand-600' : 'text-white'
                )}
                strokeWidth={2.25}
              />
            </motion.div>
          ) : (
            <motion.div
              key="chat"
              initial={{ scale: 0.6, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.6, opacity: 0 }}
              transition={{ duration: 0.2 }}
            >
              <MessageSquareText
                className={cn(
                  'w-5 h-5 sm:w-6 sm:h-6',
                  isDarkBackground ? 'text-brand-600' : 'text-white'
                )}
                strokeWidth={2}
              />
            </motion.div>
          )}
        </AnimatePresence>
      </motion.button>
    </div>
  )
}
